import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  HiSearch,
  HiPlus,
  HiX,
  HiPencil,
  HiTrash,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi';
import { FaCoins } from 'react-icons/fa';
import Toast from '../../components/toaster';
import {
  fetchKrishnaKarmaTimePlansList,
  createKrishnaKarmaTimePlan,
  updateKrishnaKarmaTimePlan,
  deactivateKrishnaKarmaTimePlan,
} from '../../services/krishnaKarmaTimePlanService';

const emptyForm = {
  title: '',
  description: '',
  minutes: '',
  karma_coins: '',
  sort_order: '',
  is_active: true,
};

const KrishnaTimePlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 12;

  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [planToDeactivate, setPlanToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const searchTimerRef = useRef(null);
  const loadingRef = useRef(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);

  const loadPlans = useCallback(async (currentPage, search) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await fetchKrishnaKarmaTimePlansList(currentPage, pageSize, search);
      const items = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res)
            ? res
            : [];
      const total =
        res?.totalCount ??
        res?.total ??
        res?.data?.totalCount ??
        items.length;

      setPlans(items);
      setTotalCount(Number(total) || 0);
    } catch (err) {
      Toast.error(err.message || 'Failed to load plans');
      setPlans([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    loadPlans(page, debouncedSearch);
  }, [page, debouncedSearch, loadPlans]);

  const openAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      ...emptyForm,
      sort_order: totalCount > 0 ? totalCount + 1 : 1,
      is_active: true,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const openEditModal = (plan) => {
    setIsEditing(true);
    setEditingId(plan._id || plan.id);
    setFormData({
      title: plan.title || '',
      description: plan.description || '',
      minutes: plan.minutes ?? '',
      karma_coins: plan.karma_coins ?? '',
      sort_order: plan.sort_order ?? '',
      is_active: typeof plan.is_active === 'boolean' ? plan.is_active : true,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setFormData(emptyForm);
    setFormErrors({});
    setEditingId(null);
  };

  const validateForm = () => {
    const errors = {};
    const title = (formData.title || '').trim();
    const description = (formData.description || '').trim();
    const minutes = Number(formData.minutes);
    const karmaCoins = Number(formData.karma_coins);
    const sortOrder = Number(formData.sort_order);

    if (!title) errors.title = 'Title is required';
    else if (title.length > 200) errors.title = 'Title max 200 characters';

    if (!description) errors.description = 'Description is required';
    else if (description.length > 2000) errors.description = 'Description max 2000 characters';

    if (formData.minutes === '' || Number.isNaN(minutes) || minutes < 1) {
      errors.minutes = 'Minutes must be an integer ≥ 1';
    }
    if (formData.karma_coins === '' || Number.isNaN(karmaCoins) || karmaCoins < 1) {
      errors.karma_coins = 'Karma coins must be an integer ≥ 1';
    }
    if (formData.sort_order === '' || Number.isNaN(sortOrder) || !Number.isInteger(sortOrder)) {
      errors.sort_order = 'Sort order is required (integer)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        minutes: Number(formData.minutes),
        karma_coins: Number(formData.karma_coins),
        sort_order: Number(formData.sort_order),
        is_active: Boolean(formData.is_active),
      };

      if (isEditing) {
        await updateKrishnaKarmaTimePlan({ plan_id: editingId, ...payload });
        Toast.success('Plan updated successfully');
      } else {
        await createKrishnaKarmaTimePlan(payload);
        Toast.success('Plan created successfully');
      }
      closeFormModal();
      await loadPlans(page, debouncedSearch);
    } catch (err) {
      Toast.error(err.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = (plan) => {
    setPlanToDeactivate(plan);
    setShowDeactivateModal(true);
  };

  const handleDeactivate = async () => {
    if (!planToDeactivate) return;
    setDeactivating(true);
    try {
      const id = planToDeactivate._id || planToDeactivate.id;
      await deactivateKrishnaKarmaTimePlan(id);
      Toast.success('Plan deactivated successfully');
      setShowDeactivateModal(false);
      setPlanToDeactivate(null);
      await loadPlans(page, debouncedSearch);
    } catch (err) {
      Toast.error(err.message || 'Failed to deactivate plan');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (plan) => {
    try {
      await updateKrishnaKarmaTimePlan({
        plan_id: plan._id || plan.id,
        is_active: true,
      });
      Toast.success('Plan activated successfully');
      await loadPlans(page, debouncedSearch);
    } catch (err) {
      Toast.error(err.message || 'Failed to activate plan');
    }
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const renderPageButtons = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, page - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="w-full max-w-[1700px] mx-auto">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="w-full sm:w-56">
          <div className="relative">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-[#ECECEC] rounded-xl bg-white focus:outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap"
        >
          <HiPlus className="w-5 h-5" />
          <span>Add Plan</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <svg
            className="w-12 h-12 animate-spin"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="time-plans-spinner" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E9D5FF" />
                <stop offset="50%" stopColor="#A855F7" />
                <stop offset="100%" stopColor="#6B21A8" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="40"
              stroke="url(#time-plans-spinner)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray="60 200"
              fill="none"
              transform="rotate(-90 50 50)"
            />
          </svg>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No time plans yet</h3>
          <p className="text-sm text-gray-500 mb-6">
            Create plans so users can redeem Karma Coins for Krishna conversation time.
          </p>
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-brand hover:opacity-90"
          >
            <HiPlus className="w-5 h-5" />
            Add Plan
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const id = plan._id || plan.id;
              const active = plan.is_active !== false;
              return (
                <div
                  key={id}
                  className="bg-white rounded-xl p-4 transition-shadow relative overflow-hidden flex flex-col"
                  style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate flex-1 min-w-0">
                      {plan.title}
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span
                        className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${
                          active
                            ? 'bg-[#9FF7C9]/30 text-[#60BC7C]'
                            : 'bg-[#FFF0F0] text-[#F76464]'
                        }`}
                      >
                        {active ? 'Active' : 'Inactive'}
                      </span>
                      {!active && (
                        <button
                          type="button"
                          onClick={() => handleReactivate(plan)}
                          className="h-8 px-2.5 text-[11px] font-bold text-brand bg-brand/10 rounded-lg hover:bg-brand/20"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openEditModal(plan)}
                        className="w-8 h-8 rounded-lg bg-[#EFEBF3] text-brand flex items-center justify-center hover:opacity-90"
                        title="Edit"
                      >
                        <HiPencil className="w-4 h-4" />
                      </button>
                      {active && (
                        <button
                          type="button"
                          onClick={() => confirmDeactivate(plan)}
                          className="w-8 h-8 rounded-lg bg-[#FFF0F0] text-[#F76464] flex items-center justify-center hover:opacity-90"
                          title="Deactivate"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 leading-snug mb-3 line-clamp-2">
                    {plan.description || '—'}
                  </p>

                  <div className="grid grid-cols-2 gap-2 mt-auto">
                    <div className="bg-[#F8F6F3] rounded-lg px-2 py-1.5">
                      <p className="text-[10px] text-gray-500 font-medium">Minutes</p>
                      <p className="text-xs font-bold text-gray-900">{plan.minutes}</p>
                    </div>
                    <div className="bg-[#F8F6F3] rounded-lg px-2 py-1.5">
                      <p className="text-[10px] text-gray-500 font-medium">Karma</p>
                      <p className="text-xs font-bold text-gray-900 flex items-center gap-1">
                        {plan.karma_coins}
                        <FaCoins className="w-2.5 h-2.5 text-brand/70" />
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalCount > pageSize && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg text-gray-600 hover:bg-white disabled:opacity-40"
              >
                <HiChevronLeft className="w-4 h-4" />
              </button>
              {renderPageButtons().map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-semibold ${
                    p === page ? 'bg-brand text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg text-gray-600 hover:bg-white disabled:opacity-40"
              >
                <HiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Add / Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">
                {isEditing ? 'Edit Time Plan' : 'Add Time Plan'}
              </h2>
              <button
                type="button"
                onClick={closeFormModal}
                disabled={saving}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  maxLength={200}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    formErrors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g. Short session"
                />
                {formErrors.title && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  maxLength={2000}
                  rows={3}
                  onChange={(e) => updateField('description', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none ${
                    formErrors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Shown to users in the app"
                />
                {formErrors.description && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Minutes <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.minutes}
                    onChange={(e) =>
                      updateField('minutes', e.target.value.replace(/[^0-9]/g, ''))
                    }
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                      formErrors.minutes ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="≥ 1"
                  />
                  {formErrors.minutes && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.minutes}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Karma Coins <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.karma_coins}
                    onChange={(e) =>
                      updateField('karma_coins', e.target.value.replace(/[^0-9]/g, ''))
                    }
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                      formErrors.karma_coins ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="≥ 1"
                  />
                  {formErrors.karma_coins && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.karma_coins}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Sort Order <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.sort_order}
                  onChange={(e) =>
                    updateField('sort_order', e.target.value.replace(/[^0-9-]/g, ''))
                  }
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    formErrors.sort_order ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Lower = first in app"
                />
                {formErrors.sort_order && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.sort_order}</p>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={Boolean(formData.is_active)}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                  className="rounded accent-brand"
                />
                Active (visible & redeemable in app)
              </label>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeFormModal}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate confirm */}
      {showDeactivateModal && planToDeactivate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-2">Deactivate plan?</h2>
            <p className="text-sm text-gray-500 mb-6">
              <span className="font-semibold text-gray-700">{planToDeactivate.title}</span> will be
              hidden from the app and cannot be redeemed. You can activate it again later.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!deactivating) {
                    setShowDeactivateModal(false);
                    setPlanToDeactivate(null);
                  }
                }}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {deactivating ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KrishnaTimePlansTab;
